import { Link } from 'react-router-dom';
import { Users, Hash, Shield, Crown, Sparkles, ShieldAlert, Trophy, Activity, Webhook, ArrowUpRight, Zap } from 'lucide-react';

interface OverviewPanelProps {
  guildData: any;
  guild: any;
}

function StatCard({
  label,
  value,
  icon: Icon,
  gradient,
  glowColor,
}: {
  label: string;
  value: number | null | undefined;
  icon: any;
  gradient: string;
  glowColor: string;
}) {
  const loaded = value !== null && value !== undefined;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-dark-800/40 backdrop-blur-sm p-5 group hover:border-white/[0.12] transition-all duration-500 hover:shadow-xl" style={{ ['--glow' as string]: glowColor }}>
      {/* Ambient glow on hover */}
      <div className={`absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-[0.12] blur-2xl transition-all duration-700`} />
      <div className={`absolute -top-4 -left-4 w-16 h-16 rounded-full bg-gradient-to-br ${gradient} opacity-[0.04] blur-xl`} />

      {/* Icon */}
      <div className={`relative inline-flex p-2.5 rounded-xl bg-gradient-to-br ${gradient} mb-4 shadow-lg shadow-black/20`}>
        <Icon className="w-4 h-4 text-white" />
        <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${gradient} opacity-50 blur-md -z-10`} />
      </div>

      {/* Value */}
      {loaded ? (
        <p className="text-3xl font-bold text-white tabular-nums tracking-tight">{value!.toLocaleString()}</p>
      ) : value === undefined ? (
        <div className="h-9 w-20 rounded-lg bg-white/[0.04] animate-pulse mb-0.5" />
      ) : (
        <p className="text-3xl font-bold text-gray-600 tabular-nums tracking-tight" title="Requires Presence Intent — enable in Discord Developer Portal">N/A</p>
      )}
      <p className="text-[11px] text-gray-500 mt-1.5 font-medium uppercase tracking-wider">{label}</p>
    </div>
  );
}

const quickLinks = [
  {
    path: 'welcome',
    label: 'Welcome System',
    desc: 'Greetings, DM messages & embeds',
    icon: Crown,
    gradient: 'from-pink-500 to-rose-600',
    accent: '#f43f5e',
  },
  {
    path: 'moderation',
    label: 'Moderation',
    desc: 'Auto-mod, word filters & anti-nuke',
    icon: ShieldAlert,
    gradient: 'from-red-500 to-orange-600',
    accent: '#ef4444',
  },
  {
    path: 'pokemon',
    label: 'Pokémon',
    desc: 'Pokétwo spawns, pings & collections',
    icon: Sparkles,
    gradient: 'from-violet-500 to-purple-600',
    accent: '#8b5cf6',
  },
  {
    path: 'leveling',
    label: 'Leveling',
    desc: 'XP, level roles & multipliers',
    icon: Trophy,
    gradient: 'from-amber-500 to-yellow-600',
    accent: '#f59e0b',
  },
  {
    path: 'roles',
    label: 'Members',
    desc: 'View and manage server roles',
    icon: Shield,
    gradient: 'from-blue-500 to-cyan-600',
    accent: '#3b82f6',
  },
  {
    path: 'webhooks',
    label: 'Webhooks',
    desc: 'Send & edit webhook messages',
    icon: Webhook,
    gradient: 'from-teal-500 to-emerald-600',
    accent: '#14b8a6',
  },
];

export default function OverviewPanel({ guildData, guild }: OverviewPanelProps) {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* ─── Server Banner ─── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-dark-800/40 backdrop-blur-sm">
        {/* Decorative mesh */}
        <div className="absolute inset-0 mesh-gradient opacity-60 pointer-events-none" />
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary/8 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-secondary/8 blur-3xl pointer-events-none" />

        <div className="relative p-7">
          <div className="flex items-center gap-5">
            {guild.icon ? (
              <div className="relative">
                <img
                  src={guild.icon}
                  alt={guild.name}
                  className="w-[72px] h-[72px] rounded-2xl ring-2 ring-white/10 shadow-2xl shadow-black/30"
                />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-[3px] border-dark-900 shadow-lg" />
              </div>
            ) : (
              <div className="relative">
                <div className="w-[72px] h-[72px] rounded-2xl bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center text-white font-bold text-2xl shadow-2xl">
                  {guild.name.charAt(0)}
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-[3px] border-dark-900 shadow-lg" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-display font-bold text-white tracking-tight mb-1">
                {guildData?.name || guild.name}
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-gray-500 text-sm">
                  <Zap className="w-3.5 h-3.5 text-primary" />
                  Server Dashboard
                </span>
                <span className="text-gray-700 text-[11px] font-mono">ID: {guild.id}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Stats Grid ─── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Activity className="w-3 h-3 text-primary/60" />
          Live Stats
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Members"  value={guildData?.memberCount}     icon={Users}    gradient="from-blue-500 to-cyan-500"     glowColor="rgba(59, 130, 246, 0.15)" />
          <StatCard label="Online"   value={guildData?.presenceCount}   icon={Activity}  gradient="from-green-500 to-emerald-500" glowColor="rgba(16, 185, 129, 0.15)" />
          <StatCard label="Channels" value={guildData?.channels?.length} icon={Hash}     gradient="from-violet-500 to-purple-500" glowColor="rgba(139, 92, 246, 0.15)" />
          <StatCard label="Roles"    value={guildData?.roles?.length}    icon={Shield}   gradient="from-orange-500 to-amber-500"  glowColor="rgba(245, 158, 11, 0.15)" />
        </div>
      </div>

      {/* ─── Quick Access ─── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-primary/60" />
          Quick Access
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {quickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={`/dashboard/${guild.id}/${link.path}`}
                className="group relative flex items-start gap-4 p-4 rounded-2xl bg-dark-800/30 hover:bg-dark-800/60 border border-white/[0.05] hover:border-white/[0.12] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl overflow-hidden"
              >
                {/* Ambient glow */}
                <div
                  className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 blur-2xl transition-all duration-500 pointer-events-none"
                  style={{ background: link.accent + '15' }}
                />

                <div className={`relative p-2.5 rounded-xl bg-gradient-to-br ${link.gradient} shadow-lg shrink-0 mt-0.5`}>
                  <Icon className="w-4 h-4 text-white" />
                  <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${link.gradient} opacity-40 blur-md -z-10`} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-sm group-hover:text-primary transition-colors duration-200">{link.label}</p>
                    <ArrowUpRight className="w-3 h-3 text-gray-700 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">{link.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
